import { useCallback, useContext } from 'react';

import { ModalContext } from '../ui/components2/ModalManager';
import useCurrentUser from './useCurrentUser';
import { isPublicationCurfewBlocked } from '../libs/publicationCurfew';

// Nom d'enregistrement dans `modalTypes` (src/index.js).
export const PUBLICATION_CURFEW_MODAL = 'PublicationCurfewModal';

/**
 * Gate du couvre-feu de publication (20h → 8h, cf. libs/publicationCurfew.js).
 *
 * Volontairement SÉPARÉ de `useRestriction` : ce dernier arbitre des droits attachés au compte
 * (capabilities, paywall, modération) et conclut par une modale d'offres. Le couvre-feu, lui, est
 * conjoncturel et se résout par la certification — l'y greffer aurait mêlé les deux discours et
 * fait courir un risque de régression aux vingt règles existantes. Là où les deux se rencontrent
 * (boutons Story / Vote), le couvre-feu est testé EN PREMIER : la nuit, on n'a rien à vendre.
 *
 * @returns {{ isBlocked: boolean, openCurfewModal: function, guard: function }}
 */
const usePublicationCurfew = () => {
    const currentUser = useCurrentUser();
    const { openModal } = useContext(ModalContext);

    // État au moment du RENDU : sert à l'affichage (ex. désactiver un bouton). L'arbitrage réel se
    // fait au clic, ci-dessous — un onglet ouvert avant la borne n'est pas re-rendu quand elle
    // sonne. Bornes et exemption viennent de `currentUser.publicationCurfew`, servi par l'API.
    const isBlocked = isPublicationCurfewBlocked(currentUser);

    const openCurfewModal = useCallback(() => {
        openModal(PUBLICATION_CURFEW_MODAL);
    }, [openModal]);

    /** Recalcule l'état À L'INSTANT de l'appel, pour les handlers (cf. `isBlocked` ci-dessus). */
    const isBlockedNow = useCallback(() => isPublicationCurfewBlocked(currentUser), [currentUser]);

    /**
     * Enveloppe le handler d'un CTA de publication : la nuit et sans certification, on ouvre la
     * modale au lieu d'exécuter l'action. L'heure est recalculée à CHAQUE clic.
     */
    const guard = useCallback((handler) => (...args) => {
        if (isBlockedNow()) {
            const [event] = args;
            if (typeof event?.preventDefault === 'function') event.preventDefault();
            openCurfewModal();
            return undefined;
        }
        return handler?.(...args);
    }, [isBlockedNow, openCurfewModal]);

    return { isBlocked, openCurfewModal, guard };
};

export default usePublicationCurfew;
